/**
 * Compliance → Service-provider registry - Phase 8 batch G item 3.
 *
 * GDPR Art 28 / CCPA §1798.140(ag) / DPDPA §11. Per-vendor record of
 * role, jurisdiction, DPA status, transfer mechanism, audit dates.
 */
import { useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  RefreshCw, Loader2, AlertTriangle, CheckCircle2, XCircle, Plus, Eye, Pause as PauseIcon,
  Building2, FileCheck2, Ban,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/primitives';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listProviders, createProvider, offboardProvider,
  type ServiceProvider, type ProviderRole, type TransferMechanism,
} from '@/lib/api';

const ROLES: ReadonlyArray<{ value: ProviderRole; label: string }> = [
  { value: 'processor', label: 'Processor (Art 28)' },
  { value: 'sub_processor', label: 'Sub-processor' },
  { value: 'joint_controller', label: 'Joint controller (Art 26)' },
  { value: 'controller', label: 'Separate controller' },
];
const MECHS: ReadonlyArray<TransferMechanism> = [
  'adequacy', 'sccs_2021', 'sccs_2010', 'bcrs', 'derogation', 'none',
];

export function ComplianceServiceProviders(): JSX.Element {
  const [items, setItems] = useState<ServiceProvider[]>([]);
  const [counts, setCounts] = useState({ active: 0, paused: 0, offboarded: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ServiceProvider | null>(null);

  const [vendorName, setVendorName] = useState('');
  const [role, setRole] = useState<ProviderRole>('processor');
  const [jurisdiction, setJurisdiction] = useState('IN');
  const [email, setEmail] = useState('');
  const [dpaOn, setDpaOn] = useState(false);
  const [dpaSigned, setDpaSigned] = useState('');
  const [mech, setMech] = useState<TransferMechanism | ''>('');
  const [cats, setCats] = useState('');
  const [purposes, setPurposes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await listProviders({}); setItems(r.providers); setCounts(r.byStatus); }
    catch (e) { setError(e instanceof Error ? e.message : 'failed to load providers'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  async function handleCreate() {
    if (!vendorName.trim()) { setError('Vendor name required'); return; }
    if (dpaOn && !dpaSigned) { setError('DPA on file requires a signed date'); return; }
    setSubmitting(true); setError(null);
    try {
      const input: Parameters<typeof createProvider>[0] = {
        vendorName: vendorName.trim(), vendorRole: role, jurisdiction: jurisdiction.trim(),
        dpaOnFile: dpaOn,
        ...(email.trim() ? { primaryContactEmail: email.trim() } : {}),
        ...(dpaSigned ? { dpaSignedAt: new Date(dpaSigned).toISOString() } : {}),
        ...(mech ? { transferMechanism: mech } : {}),
        ...(cats.trim() ? { dataCategories: cats.split(',').map((c) => c.trim()).filter(Boolean) } : {}),
        ...(purposes.trim() ? { processingPurposes: purposes.split(',').map((c) => c.trim()).filter(Boolean) } : {}),
      };
      await createProvider(input);
      setVendorName(''); setEmail(''); setDpaOn(false); setDpaSigned(''); setMech('');
      setCats(''); setPurposes('');
      await refresh();
    } catch (e) { setError(e instanceof Error ? e.message : 'create failed'); }
    finally { setSubmitting(false); }
  }

  async function handleOffboard(p: ServiceProvider) {
    const r = window.prompt(`Offboard ${p.vendorName}? Reason:`);
    if (!r) return;
    try { await offboardProvider(p.id, r); await refresh(); }
    catch (e) { setError(e instanceof Error ? e.message : 'offboard failed'); }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Service-provider registry (GDPR Art 28 / DPDPA §11 / CCPA §1798.140(ag))"
        subtitle="Processor / sub-processor / joint-controller records with DPA status, jurisdiction, transfer mechanism, and audit dates. Required for the Art 30 ROPA join."
        badges={[
          { label: 'Art 28 register', variant: 'glass' },
          { label: 'DPA tracker', variant: 'glass' },
          ...(loading ? [{ label: 'Loading', variant: 'glass' as const }]
            : error ? [{ label: 'Load error', variant: 'warning' as const }]
            : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={<Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>}
      />
      {error && (
        <div className="glass-lite border border-warning/40 rounded-md p-3 mb-3 text-xs flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
          <div className="flex-1">{error}</div>
          <Button size="sm" variant="glass" onClick={() => setError(null)}>Dismiss</Button>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <KpiCard label="Active" value={counts.active} icon={Building2} glow={counts.active > 0 ? 'high' : 'low'} hint="processors live" />
        <KpiCard label="Paused" value={counts.paused} icon={PauseIcon} glow="medium" hint="suspended" />
        <KpiCard label="Offboarded" value={counts.offboarded} icon={Ban} glow="low" hint="closed" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2"><Plus className="h-4 w-4 text-primary" /> Add processor</CardTitle>
            <CardDescription clamp>Cross-border transfers? Pick a mechanism. DPA-on-file requires the signed date.</CardDescription>
          </CardHeader>
          <div className="space-y-3 text-xs">
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-muted-foreground block mb-0.5">Vendor name</label>
                <input type="text" value={vendorName} onChange={(e) => setVendorName(e.target.value)} maxLength={200}
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
              <div><label className="text-muted-foreground block mb-0.5">Role</label>
                <select value={role} onChange={(e) => setRole(e.target.value as ProviderRole)}
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40">
                  {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select></div>
              <div><label className="text-muted-foreground block mb-0.5">Jurisdiction</label>
                <input type="text" value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} maxLength={80}
                  placeholder="IN / US-CA / EEA / UK"
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
              <div><label className="text-muted-foreground block mb-0.5">Primary contact (optional)</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200}
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-1 text-[11px]"><input type="checkbox" checked={dpaOn} onChange={(e) => setDpaOn(e.target.checked)} /> DPA on file</label>
              <div><input type="date" value={dpaSigned} onChange={(e) => setDpaSigned(e.target.value)} disabled={!dpaOn}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40 disabled:opacity-40" /></div>
            </div>
            <div><label className="text-muted-foreground block mb-0.5">Transfer mechanism (if cross-border)</label>
              <select value={mech} onChange={(e) => setMech(e.target.value as TransferMechanism | '')}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40">
                <option value="">- none / same jurisdiction -</option>
                {MECHS.map((m) => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
              </select></div>
            <div><label className="text-muted-foreground block mb-0.5">Data categories (comma)</label>
              <input type="text" value={cats} onChange={(e) => setCats(e.target.value)}
                placeholder="email, phone, aadhaar"
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
            <div><label className="text-muted-foreground block mb-0.5">Processing purposes (comma)</label>
              <input type="text" value={purposes} onChange={(e) => setPurposes(e.target.value)}
                placeholder="kyc, billing, analytics"
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
            <div className="flex justify-end pt-1">
              <Button size="sm" data-action="disc.comp_providers.a01" onClick={() => void handleCreate()} disabled={submitting}>
                {submitting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileCheck2 className="h-3.5 w-3.5 mr-1" />}
                Register
              </Button>
            </div>
          </div>
        </GlassCard>
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3"><CardTitle>Processor registry</CardTitle>
            <CardDescription clamp>Used by Art 30 ROPA + breach-impact joins.</CardDescription></CardHeader>
          {loading && <LoadingState caption="Loading…" />}
          {!loading && items.length === 0 && (
            <div className="glass-lite rounded p-6 text-center text-xs text-muted-foreground">
              <Building2 className="h-6 w-6 mx-auto mb-2 opacity-40" /><div>No processors registered.</div>
            </div>
          )}
          {!loading && items.length > 0 && (
            <div className="space-y-2">
              {items.map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(i * 0.03, 0.3) }} className="glass-lite rounded p-3 text-xs">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={p.status === 'active' ? 'success' : p.status === 'paused' ? 'warning' : 'glass'} className="text-[10px]">{p.status}</Badge>
                        <span className="font-medium truncate">{p.vendorName}</span>
                        <Badge variant="glass" className="text-[10px]">{p.vendorRole.replace(/_/g, ' ')}</Badge>
                        <Badge variant="glass" className="text-[10px]">{p.jurisdiction}</Badge>
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        DPA: {p.dpaOnFile ? `✓ signed ${p.dpaSignedAt ? new Date(p.dpaSignedAt).toLocaleDateString() : ''}` : '- not on file'}
                        {p.transferMechanism && ` · transfer: ${p.transferMechanism.replace(/_/g, ' ')}`}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button size="sm" variant="glass" onClick={() => setDetail(p)}><Eye className="h-3 w-3" /></Button>
                      {p.status !== 'offboarded' && <Button size="sm" variant="glass" data-action="disc.comp_providers.a02" onClick={() => void handleOffboard(p)} title="Offboard"><Ban className="h-3 w-3" /></Button>}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>
      <Dialog open={detail !== null} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{detail?.vendorName ?? 'Provider'}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground">Role</div><code className="font-mono">{detail.vendorRole}</code></div>
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground">Jurisdiction</div><code className="font-mono">{detail.jurisdiction}</code></div>
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground">Status</div><Badge variant={detail.status === 'active' ? 'success' : detail.status === 'paused' ? 'warning' : 'glass'} className="text-[10px]">{detail.status}</Badge></div>
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground">Transfer</div><code className="font-mono">{detail.transferMechanism ?? '-'}</code></div>
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground">DPA signed</div><code className="font-mono">{detail.dpaSignedAt ? new Date(detail.dpaSignedAt).toLocaleDateString() : '-'}</code></div>
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground">DPA review due</div><code className="font-mono">{detail.dpaReviewDueAt ? new Date(detail.dpaReviewDueAt).toLocaleDateString() : '-'}</code></div>
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground">Last audit</div><code className="font-mono">{detail.lastAuditAt ? new Date(detail.lastAuditAt).toLocaleDateString() : '-'}</code></div>
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground">Next audit</div><code className="font-mono">{detail.nextAuditDueAt ? new Date(detail.nextAuditDueAt).toLocaleDateString() : '-'}</code></div>
              </div>
              {detail.dataCategories.length > 0 && (
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground mb-0.5">Data categories</div><div className="flex flex-wrap gap-1">{detail.dataCategories.map((c) => <Badge key={c} variant="glass" className="text-[10px]">{c}</Badge>)}</div></div>
              )}
              {detail.processingPurposes.length > 0 && (
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground mb-0.5">Processing purposes</div><div className="flex flex-wrap gap-1">{detail.processingPurposes.map((c) => <Badge key={c} variant="glass" className="text-[10px]">{c}</Badge>)}</div></div>
              )}
              {detail.offboardedAt && (
                <div className="glass-lite border border-sev-critical/40 rounded p-2 text-sev-critical">
                  <div className="font-medium">Offboarded</div>
                  <div>{new Date(detail.offboardedAt).toLocaleString()} - {detail.offboardedReason}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
